import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { track } from '../lib/analytics';

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  /* Tier 2+ consolidation: 9 competing pages → 5 primary nav items.
     Legacy /chauffeur/* routes remain accessible for SEO + deep links. */
  const primaryLinks = [
    { path: '/', label: 'Home' },
    { path: '/chauffeur/fleet', label: 'Fleet' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isLinkActive = (path) => {
    const p = location.pathname;
    if (path === '/') return p === '/';
    if (path === '/services') return p === '/services' || p === '/chauffeur' || p === '/chauffeur/services';
    if (path === '/chauffeur/fleet') return p === '/chauffeur/fleet';
    if (path === '/contact') return p === '/contact' || p === '/chauffeur/book' || p === '/chauffeur/rates';
    return p === path;
  };

  const linkStyle = (active) => ({
    fontSize: '13px',
    fontWeight: active ? 500 : 400,
    letterSpacing: '0.8px',
    color: active ? '#C2A878' : '#E6E6E6',
    transition: 'color 180ms ease',
    textDecoration: 'none',
    position: 'relative',
    paddingBottom: '4px',
  });

  const renderUnderline = (active) =>
    active ? (
      <span style={{
        position: 'absolute', bottom: '0', left: '25%', right: '25%',
        height: '1px', background: 'rgba(194,168,120,0.4)',
      }} />
    ) : null;

  const renderLink = (link) => {
    const active = isLinkActive(link.path);
    return (
      <Link
        key={link.path}
        to={link.path}
        data-testid={`nav-link-${link.label.toLowerCase()}`}
        style={linkStyle(active)}
        onMouseEnter={(e) => { if (!active) e.target.style.color = '#C2A878'; }}
        onMouseLeave={(e) => { if (!active) e.target.style.color = '#E6E6E6'; }}
      >
        {link.label}
        {renderUnderline(active)}
      </Link>
    );
  };

  return (
    <nav
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        height: '100px',
        backgroundColor: '#080808',
        backgroundImage: 'linear-gradient(180deg, rgba(12,12,12,1) 0%, rgba(8,8,8,0.95) 50%, rgba(6,6,6,0.9) 100%)',
        boxShadow: '0px 6px 20px rgba(0,0,0,0.4), 0px 1px 0px rgba(194,168,120,0.04)',
      }}
    >
      {/* Grain texture */}
      <div className="absolute inset-0 pointer-events-none" style={{
        opacity: 0.035,
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'repeat', backgroundSize: '256px 256px',
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 18% 50%, rgba(194,168,120,0.025) 0%, transparent 50%)',
      }} />

      <div className="max-w-7xl mx-auto h-full flex items-center justify-between" style={{ paddingLeft: 'clamp(20px, 4vw, 48px)', paddingRight: 'clamp(20px, 4vw, 48px)' }}>
        {/* Logo */}
        <Link to="/" className="flex items-center relative" data-testid="nav-logo-link" style={{ marginLeft: '-8px' }}>
          <img src="/logo-tr.png" alt="Toronto Royale" width="180" height="72" fetchpriority="high" decoding="async" style={{ height: '72px', width: 'auto', transition: 'opacity 0.4s ease', opacity: 0.95 }}
            onMouseEnter={(e) => { e.target.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.target.style.opacity = '0.95'; }}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center" style={{ gap: '40px' }}>
          {primaryLinks.map(renderLink)}

          <Link
            to="/contact"
            data-testid="nav-book-ride-btn"
            onClick={() => track('click_book_cta', { location: 'nav_desktop' })}
            style={{
              fontSize: '13px', fontWeight: 400, letterSpacing: '0.8px',
              padding: '10px 24px', marginLeft: '12px',
              border: '1px solid rgba(194,168,120,0.3)', color: '#C2A878',
              textDecoration: 'none', transition: 'all 400ms ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(194,168,120,0.08)';
              e.target.style.borderColor = 'rgba(194,168,120,0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'rgba(194,168,120,0.3)';
            }}
          >
            BOOK YOUR RIDE
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden"
          style={{ color: '#E6E6E6', padding: '8px' }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          backgroundColor: '#0D0D0D',
          borderTop: isMobileMenuOpen ? '1px solid rgba(194,168,120,0.08)' : 'none',
        }}
      >
        <div style={{ padding: '32px 36px' }} className="space-y-6">
          {primaryLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              style={{
                display: 'block', fontSize: '15px', fontWeight: 400, letterSpacing: '0.8px',
                color: isLinkActive(link.path) ? '#C2A878' : '#E6E6E6',
                textDecoration: 'none', transition: 'color 180ms ease',
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/contact"
            data-testid="mobile-book-ride-btn"
            style={{
              display: 'block', textAlign: 'center', fontSize: '13px', fontWeight: 400,
              letterSpacing: '0.8px', padding: '12px 24px', marginTop: '16px',
              border: '1px solid rgba(194,168,120,0.3)', color: '#C2A878', textDecoration: 'none',
            }}
            onClick={() => { track('click_book_cta', { location: 'nav_mobile_menu' }); setIsMobileMenuOpen(false); }}
          >
            BOOK YOUR RIDE
          </Link>
        </div>
      </div>
    </nav>
  );
};
